import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Html5QrcodeScanner } from "html5-qrcode";
import { QrCode, CheckCircle, XCircle, Camera, Loader2, Users, Search, Wifi } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export default function Scanner() {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("home");
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState("");
  const [marking, setMarking] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeScannerRef = useRef(null);

  useEffect(() => {
    if (mode === "manual") {
      axios.get(`${API}/workers`).then(r => setWorkers(r.data)).catch(() => toast.error("Erreur chargement ouvriers"));
    }
  }, [mode]);

  const startScanner = () => {
    setMode("scan");
    setScanning(true);
    setScanResult(null);
    setTimeout(() => {
      if (scannerRef.current && !html5QrCodeScannerRef.current) {
        const initScanner = (videoConstraints) => {
          html5QrCodeScannerRef.current = new Html5QrcodeScanner(
            "qr-reader",
            { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0, showTorchButtonIfSupported: true, videoConstraints },
            false
          );
          html5QrCodeScannerRef.current.render(onScanSuccess, (err) => {
            if (err && err.name === "OverconstrainedError" && videoConstraints.facingMode) {
              html5QrCodeScannerRef.current.clear();
              html5QrCodeScannerRef.current = null;
              initScanner({ facingMode: "user" });
            }
          });
        };
        initScanner({ facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } });
      }
    }, 100);
  };

  const stopScanner = () => {
    if (html5QrCodeScannerRef.current) {
      html5QrCodeScannerRef.current.clear();
      html5QrCodeScannerRef.current = null;
    }
    setScanning(false);
    setMode("home");
  };

  const onScanSuccess = async (decodedText) => {
    setLoading(true);
    stopScanner();
    try {
      const response = await axios.post(`${API}/attendance`, { matricule: decodedText });
      setScanResult({ success: true, data: response.data });
      toast.success("Presence enregistree pour " + response.data.worker_name);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l enregistrement";
      setScanResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const onScanFailure = () => {};

  const markManual = async (worker) => {
    setMarking(worker.id);
    try {
      const response = await axios.post(`${API}/attendance`, { matricule: worker.matricule });
      setScanResult({ success: true, data: response.data, manual: true });
      setMode("result");
      toast.success("Presence enregistree pour " + worker.name);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l enregistrement";
      toast.error(errorMsg);
    } finally {
      setMarking(null);
    }
  };

  const reset = () => {
    setScanResult(null);
    setSearch("");
    setMode("home");
  };

  useEffect(() => {
    return () => {
      if (html5QrCodeScannerRef.current) html5QrCodeScannerRef.current.clear();
    };
  }, []);

  const filteredWorkers = workers.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    w.matricule.toLowerCase().includes(search.toLowerCase())
  );

  const statusLabel = (status) => {
    if (!status) return "";
    if (status === "on_time") return "A l heure";
    if (status === "late") return "En retard";
    return "Absent";
  };

  const statusColor = (status) => {
    if (status === "on_time") return "text-emerald-400";
    if (status === "late") return "text-amber-400";
    return "text-rose-400";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-5">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="bg-orange-100 p-2.5 rounded-xl">
            <QrCode className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-manrope font-bold text-slate-900 tracking-tight" data-testid="scanner-title">
              Scanner QR Code
            </h1>
            <p className="text-sm text-slate-500">Scannez le QR ou marquez manuellement les membres</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Home — 2 boutons */}
        {mode === "home" && !scanResult && !loading && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center hover:shadow-md transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-2xl mb-5">
                <Camera className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-manrope font-bold text-slate-900 mb-2">Scanner QR Code</h3>
              <p className="text-slate-500 mb-6 text-sm">Pour les membres présents physiquement</p>
              <Button size="lg" onClick={startScanner} data-testid="start-scan-button"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 font-semibold rounded-xl">
                <Camera className="mr-2 h-5 w-5" />Démarrer le Scan
              </Button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center hover:shadow-md transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-2xl mb-5">
                <Wifi className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="text-xl font-manrope font-bold text-slate-900 mb-2">Présence Manuelle</h3>
              <p className="text-slate-500 mb-6 text-sm">Pour les membres qui suivent en ligne</p>
              <Button size="lg" onClick={() => setMode("manual")}
                className="bg-slate-800 hover:bg-slate-900 text-white px-8 font-semibold rounded-xl">
                <Users className="mr-2 h-5 w-5" />Marquer Présent
              </Button>
            </div>
          </div>
        )}

        {/* Mode manuel — liste avec recherche */}
        {mode === "manual" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-manrope font-bold text-slate-900 flex items-center gap-2">
                <Wifi className="h-5 w-5 text-sky-500" />Membres en ligne
              </h3>
              <button onClick={() => setMode("home")} className="text-sm text-slate-400 hover:text-slate-700 font-medium transition-colors">Annuler</button>
            </div>
            <div className="p-4">
              {/* Recherche */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou matricule..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                />
              </div>

              {/* Liste */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredWorkers.length === 0 ? (
                  <p className="text-slate-400 text-center py-8 text-sm">Aucun membre trouvé</p>
                ) : (
                  filteredWorkers.map(worker => (
                    <div key={worker.id} className="flex items-center justify-between bg-slate-50 hover:bg-orange-50 rounded-xl px-4 py-3 border border-slate-100 hover:border-orange-200 transition-colors">
                      <div>
                        <p className="text-slate-900 font-semibold text-sm">{worker.name}</p>
                        <p className="text-slate-400 text-xs font-mono mt-0.5">{worker.matricule}</p>
                      </div>
                      <button
                        onClick={() => markManual(worker)}
                        disabled={marking === worker.id}
                        className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors"
                      >
                        {marking === worker.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle className="h-3 w-3" />
                        )}
                        {marking === worker.id ? "..." : "Présent"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scanner actif — zone caméra reste sombre */}
        {mode === "scan" && scanning && (
          <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-xl">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-white text-sm font-semibold">Caméra active</span>
            </div>
            <div ref={scannerRef} id="qr-reader" data-testid="qr-reader" className="overflow-hidden"></div>
            <div className="px-5 py-4 text-center">
              <Button variant="outline" onClick={stopScanner} data-testid="stop-scan-button"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">Annuler</Button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 text-center">
            <Loader2 className="h-12 w-12 text-orange-500 mx-auto mb-4 animate-spin" />
            <p className="text-slate-700 font-semibold">Enregistrement en cours...</p>
          </div>
        )}

        {/* Resultat */}
        {(scanResult || mode === "result") && !loading && (
          <div className={
            "rounded-2xl shadow-sm border overflow-hidden " +
            (scanResult && scanResult.success
              ? "bg-emerald-50 border-emerald-200"
              : "bg-rose-50 border-rose-200")
          } data-testid="scan-result">
            <div className={
              "px-6 py-5 text-center " +
              (scanResult && scanResult.success ? "border-b border-emerald-100" : "border-b border-rose-100")
            }>
              {scanResult && scanResult.success ? (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-2xl mb-4">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-manrope font-bold text-emerald-900 mb-1">Présence Enregistrée !</h3>
                  {scanResult.manual && (
                    <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs font-semibold px-3 py-1 rounded-full">
                      <Wifi className="h-3 w-3" />Présence manuelle (en ligne)
                    </span>
                  )}
                </>
              ) : (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-2xl mb-4">
                    <XCircle className="h-8 w-8 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-manrope font-bold text-rose-900 mb-1">Échec</h3>
                  <p className="text-rose-600 text-sm">{scanResult && scanResult.message}</p>
                </>
              )}
            </div>

            {scanResult && scanResult.success && (
              <div className="px-6 py-5 grid grid-cols-2 gap-4">
                {[
                  { label: "Matricule", value: scanResult.data.matricule, mono: true },
                  { label: "Nom", value: scanResult.data.worker_name },
                  { label: "Département", value: scanResult.data.department_name || "N/A" },
                  { label: "Heure", value: scanResult.data.time.substring(0, 5), mono: true },
                ].map(({ label, value, mono }) => (
                  <div key={label} className="bg-white rounded-xl p-3 border border-emerald-100">
                    <p className="text-xs text-slate-400 mb-1">{label}</p>
                    <p className={"text-sm font-bold text-slate-900 " + (mono ? "font-mono" : "")}>{value}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="px-6 pb-6 flex gap-3 justify-center flex-wrap">
              <Button size="lg" onClick={reset}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6 font-semibold rounded-xl">
                <Camera className="mr-2 h-5 w-5" />Scanner à Nouveau
              </Button>
              <Button size="lg" onClick={() => { setScanResult(null); setSearch(""); setMode("manual"); }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 font-semibold rounded-xl">
                <Users className="mr-2 h-5 w-5" />Présence Manuelle
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}